import type { TFunction } from 'i18next';

/** A pause this long between two messages on the same day gets its own "Today, 9:40 AM" divider. */
export const GAP_MS = 60 * 60 * 1000;

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
const DAY_MS = 24 * 60 * 60 * 1000;

export const localeFor = (language: string) => (language === 'hi' ? 'hi-IN' : 'en-IN');

/** A divider goes before the first message, when the day changes, and after a long pause. */
export function needsDivider(previous: Date | null, at: Date): boolean {
  return !previous || startOfDay(previous) !== startOfDay(at) || at.getTime() - previous.getTime() >= GAP_MS;
}

/** "Today", "Yesterday", "Monday" (this week) or "28 Aug" / "28 Aug 2025". */
export function dayLabel(at: Date, now: Date, t: TFunction, locale: string): string {
  const days = Math.round((startOfDay(now) - startOfDay(at)) / DAY_MS);
  if (days === 0) return t('chat.today');
  if (days === 1) return t('chat.yesterday');
  if (days > 1 && days < 7) return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(at);
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(at);
}

export const timeLabel = (at: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(at);

/** The first divider names the day; later ones (a return after a pause) add the time. */
export function dividerLabel(at: Date, first: boolean, now: Date, t: TFunction, locale: string): string {
  const day = dayLabel(at, now, t, locale);
  return first ? day : t('chat.dayAt', { day, time: timeLabel(at, locale) });
}

/** For lists of chats: the time today, else the day. */
export function whenLabel(at: Date, now: Date, t: TFunction, locale: string): string {
  return startOfDay(at) === startOfDay(now) ? timeLabel(at, locale) : dayLabel(at, now, t, locale);
}
